import React, { FormEventHandler, useEffect, useState } from 'react';
import {
  EmailStep,
  FormField as Item,
  FormState,
} from '@zapier/product-get-help';

import { useFormContext } from 'app/get-help/contexts/FormContext';
import { getFromForcedObject, setInForcedObject } from 'app/get-help/utils';

import Upgrade from './Upgrade';

type ContactProps = {
  answer: (stepData?: any) => void;
  canContinue: boolean;
  data: any;
  isEligible: boolean;
  isSubmitting: boolean;
  items: Item[];
  setMeta: (key: string, value: string) => void;
  submit: () => Promise<void>;
};

const Contact = ({
  answer,
  canContinue: hasCompletedRequiredFields,
  data,
  isEligible: isAlreadyEligible,
  isSubmitting,
  items = [],
  setMeta,
  submit,
}: ContactProps) => {
  const {
    account,
    isCriticalTicketEligible,
    isCriticalIssue,
    setIsCriticalIssue,
  } = useFormContext();

  // Store in state so that the form can be enabled after the user finishes upgrading.
  const [isEligible, setIsEligible] = useState(isAlreadyEligible);

  // needsToUpgrade ------------------------------------------------------------
  const [needsToUpgrade, setNeedsToUpgrade] = useState(false);
  useEffect(() => {
    setNeedsToUpgrade(Boolean(!isEligible && account));
  }, [account, isEligible]);

  // formState -----------------------------------------------------------------
  const [formState, setFormState] = useState<FormState>('initial');
  useEffect(() => {
    let state: FormState = 'initial';
    if (isSubmitting) {
      state = 'submitting';
    } else if (hasCompletedRequiredFields && !needsToUpgrade) {
      state = 'readyToSubmit';
    }
    setFormState(state);
  }, [hasCompletedRequiredFields, isSubmitting, needsToUpgrade]);

  // fields --------------------------------------------------------------------
  const createOnChangeField = (id: string) => (value: string) => {
    answer(setInForcedObject(data, id, value));
    // NOTE: We could make email and name a field-type to decouple this.
    if (value && (id === 'email' || id === 'full_name')) {
      setMeta(id, value);
    }
  };
  const fields = items.map(item => ({
    onChange: createOnChangeField(item.id),
    staticProperties: item,
    value: getFromForcedObject(data, item.id) || '',
  }));

  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    submit();
  };

  return (
    <form onSubmit={handleSubmit}>
      <EmailStep
        fields={fields}
        formState={formState}
        isCriticalTicketEligible={isCriticalTicketEligible}
        isCriticalIssue={isCriticalIssue}
        setIsCriticalIssue={setIsCriticalIssue}
        setMeta={setMeta}
      />
      {needsToUpgrade && <Upgrade onUpgrade={() => setIsEligible(true)} />}
    </form>
  );
};

export default Contact;
